import {
  AttachProductFileResult,
  OkResponse,
  PRODUCT_FILE_GROUPS,
  type Product,
  ProductFile,
  ProductFileList,
  type ProductFileGroup,
  type ProfileDiff,
  type StoredFile,
  Variant,
  formatBRL,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { ApiClientError, api } from '../../lib/api';
import { formatBytes } from '../../lib/backups-api';
import { fileContentUrl } from '../../lib/files-api';
import { errorMessage } from '../../lib/messages';
import { formatGrams, formatHm } from '../../lib/numbers';
import { productsKey, useInvalidateProducts } from '../../lib/products-api';
import { useIsAdmin } from '../../lib/session';
import {
  type UploadHandle,
  type UploadStatus,
  progressLabel,
  startUpload,
} from '../../lib/tus-upload';

const DuplicateDetails = z.object({
  duplicate: z.object({ name: z.string(), version: z.number() }),
});

const filesKey = (id: string) => [...productsKey, id, 'files'];

export function useProductFiles(productId: string) {
  return useQuery({
    queryKey: filesKey(productId),
    queryFn: () => api.get(`/products/${productId}/files`, ProductFileList),
  });
}

/** Where a picked file goes, by extension. */
export function groupFor(name: string): ProductFileGroup {
  const n = name.toLowerCase();
  if (n.endsWith('.gcode') || n.endsWith('.gcode.3mf')) return 'sliced';
  if (/\.(stl|3mf|obj)$/.test(n)) return 'model';
  if (/\.(step|stp|f3d|f3z|blend|scad|fcstd|skp)$/.test(n)) return 'source';
  return 'document';
}

const lineage = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');
const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const baseName = (name: string) => name.replace(/\.gcode\.3mf$|\.gcode$|\.3mf$/i, '');

interface Pending {
  key: string;
  file: File;
  group: ProductFileGroup;
  status: UploadStatus;
  sent: number;
  total: number;
  error?: string;
  stored?: StoredFile;
  handle?: UploadHandle;
}

function UploadDialog({
  product,
  existing,
  onClose,
  onSaved,
}: {
  product: Product;
  existing: ProductFile[];
  onClose: () => void;
  onSaved: (
    results: {
      pending: Pending;
      result?: AttachProductFileResult;
      duplicate?: { name: string; version: number };
    }[],
  ) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Pending[]>([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const patch = (key: string, p: Partial<Pending>) =>
    setItems((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const add = (picked: FileList | null) => {
    for (const file of Array.from(picked ?? [])) {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      const group = groupFor(file.name);
      const handle = startUpload(file, group === 'sliced' ? 'sliced' : group, {
        progress: (sent, total) => patch(key, { sent, total }),
        status: (status, error) => patch(key, { status, error }),
      });
      void handle.done.then((stored) => patch(key, { stored })).catch(() => undefined);
      setItems((list) => [
        ...list,
        { key, file, group, status: 'uploading', sent: 0, total: file.size, handle },
      ]);
    }
  };
  const nextVersion = (p: Pending) => {
    const same = existing.filter((f) => f.group === p.group && f.lineage === lineage(p.file.name));
    return same.length ? Math.max(...same.map((f) => f.version)) + 1 : null;
  };
  const versioned = items.find((p) => nextVersion(p) !== null);
  const busy = items.some(
    (p) => p.status === 'uploading' || p.status === 'offline' || p.status === 'paused',
  );
  const ready =
    items.length > 0 &&
    items.every((p) => p.stored || p.status === 'error') &&
    items.some((p) => p.stored);

  const save = async () => {
    setSaving(true);
    const out: Parameters<typeof onSaved>[0] = [];
    for (const p of items.filter((x) => x.stored)) {
      try {
        const result = await api.post(`/products/${product.id}/files`, AttachProductFileResult, {
          fileId: p.stored!.id,
          group: p.group,
          changeNote: nextVersion(p) !== null ? note.trim() || null : null,
        });
        out.push({ pending: p, result });
      } catch (err) {
        const parsed =
          err instanceof ApiClientError && err.status === 409
            ? DuplicateDetails.safeParse(err.details)
            : null;
        const dup = parsed?.success ? parsed.data.duplicate : undefined;
        if (dup) out.push({ pending: p, duplicate: dup });
        else out.push({ pending: { ...p, error: errorMessage(err) } });
      }
    }
    setSaving(false);
    onSaved(out);
  };

  return (
    <Modal
      title="Enviar arquivos"
      wide
      onClose={() => {
        for (const p of items) if (!p.stored) p.handle?.abort();
        onClose();
      }}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={!ready || busy || saving}
            onClick={() => void save()}
          >
            {items.length === 0
              ? 'Escolha arquivos'
              : busy
                ? 'Enviando…'
                : saving
                  ? 'Salvando…'
                  : 'Salvar'}
          </button>
        </>
      }
    >
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
      <div
        className="dropzone"
        style={{ cursor: 'pointer' }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          add(e.dataTransfer.files);
        }}
      >
        <Icon name="box" size={30} />
        <p style={{ fontWeight: 500, color: 'var(--ink)' }}>
          Arraste G-code, 3MF, STL, STEP, F3D, Blender ou PDF
        </p>
        <p className="xs">Arquivo com o mesmo nome vira nova versão. Idênticos são avisados.</p>
        <button type="button" className="btn p" onClick={() => input.current?.click()}>
          Escolher arquivos
        </button>
      </div>
      {items.map((p) => {
        const v = nextVersion(p);
        const pct = p.total ? Math.round((p.sent / p.total) * 100) : 0;
        const done = !!p.stored;
        return (
          <div
            key={p.key}
            className="card sunk col"
            style={{ gap: 8, padding: 12, borderRadius: 12 }}
          >
            <div className="row between" style={{ gap: 10 }}>
              <span className="trunc" style={{ fontWeight: 500 }}>
                {p.file.name}
              </span>
              <span className="row" style={{ gap: 8, flex: '0 0 auto' }}>
                {done ? (
                  <span className="badge ok">Enviado</span>
                ) : p.status === 'error' ? (
                  <span className="badge err">Falhou</span>
                ) : p.status === 'paused' ? (
                  <span className="badge warn">Pausado</span>
                ) : (
                  <span className="xs muted tab">{progressLabel(p.sent, p.total)}</span>
                )}
                {!done && p.status !== 'error' ? (
                  <button
                    type="button"
                    className="btn sm g"
                    onClick={() => (p.status === 'paused' ? p.handle?.resume() : p.handle?.pause())}
                  >
                    {p.status === 'paused' ? 'Retomar' : 'Pausar'}
                  </button>
                ) : null}
              </span>
            </div>
            {!done && p.status !== 'error' ? (
              <div className="layers thin">
                <div className="fill" style={{ width: `${pct}%` }} />
                <div className="nozzle" style={{ left: `${pct}%` }} />
              </div>
            ) : null}
            {p.status === 'offline' ? (
              <p className="xs" style={{ color: 'var(--warn)' }}>
                Sem sinal. Continua de onde parou quando a conexão voltar.
              </p>
            ) : null}
            {p.error ? (
              <p className="xs" style={{ color: 'var(--err)' }}>
                {p.error}
              </p>
            ) : null}
            <div className="row wrap" style={{ gap: 8 }}>
              <select
                className="input"
                style={{ height: 34, width: 'auto' }}
                aria-label={`Grupo de ${p.file.name}`}
                value={p.group}
                onChange={(e) =>
                  patch(p.key, {
                    group:
                      PRODUCT_FILE_GROUPS.find((g) => g.group === e.target.value)?.group ?? p.group,
                  })
                }
              >
                {PRODUCT_FILE_GROUPS.map((g) => (
                  <option key={g.group} value={g.group}>
                    {g.label.split(' · ')[0]}
                  </option>
                ))}
              </select>
              <span className="xs muted">
                {v !== null ? `Nova versão de ${p.file.name} → v${v}` : 'Arquivo novo'}
              </span>
            </div>
          </div>
        );
      })}
      {versioned ? (
        <div className="field">
          <label htmlFor="pf-note">Nota da mudança</label>
          <input
            id="pf-note"
            className="input"
            placeholder="Ex.: costura atrás, 3 paredes"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="help">Aparece no histórico de versões.</span>
        </div>
      ) : null}
    </Modal>
  );
}

/** "Arquivos" tab (board Produto-Visao, Arquivos). */
export function FilesTab({ product }: { product: Product }) {
  const files = useProductFiles(product.id);
  const isAdmin = useIsAdmin();
  const toast = useToast();
  const qc = useQueryClient();
  const invalidateProducts = useInvalidateProducts();
  const [uploading, setUploading] = useState(false);
  const [diffs, setDiffs] = useState<ProfileDiff[]>([]);
  const [dups, setDups] = useState<
    { fileId: string; name: string; version: number; group: ProductFileGroup }[]
  >([]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: filesKey(product.id) });
    invalidateProducts();
  };
  const makeCurrent = useMutation({
    mutationFn: (id: string) => api.post(`/product-files/${id}/make-current`, ProductFile),
    onSuccess: (f) => {
      refresh();
      toast(`v${f.version} agora é a atual de ${f.name}`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/product-files/${id}`, OkResponse),
    onSuccess: () => {
      refresh();
      toast('Versão excluída');
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const apply = useMutation({
    mutationFn: (d: ProfileDiff) =>
      api.post(`/variants/${d.variantId}/apply-sliced`, Variant, {
        productFileId: d.productFileId,
      }),
    onSuccess: (v, d) => {
      refresh();
      setDiffs((list) => list.filter((x) => x !== d));
      toast(
        `Perfil da ${v.name} atualizado${v.cost ? ` · custo ${formatBRL(v.cost.unitCostCentavos)}` : ''}`,
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const keepCopy = useMutation({
    mutationFn: (d: (typeof dups)[number]) =>
      api.post(`/products/${product.id}/files`, AttachProductFileResult, {
        fileId: d.fileId,
        group: d.group,
        onDuplicate: 'copy',
      }),
    onSuccess: (_r, d) => {
      refresh();
      setDups((list) => list.filter((x) => x !== d));
      toast('Enviado como cópia');
    },
    onError: (err) => toast(errorMessage(err)),
  });

  const list = files.data?.data ?? [];
  const versionName = (id: string) => {
    const f = list.find((x) => x.id === id);
    return f ? `${baseName(f.name)} v${f.version}` : 'o arquivo';
  };
  const cols = '1.8fr 110px 1.4fr 110px 80px 210px';

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="row between wrap" style={{ gap: 10 }}>
        <p className="sm muted">
          Fatiados, modelos e fontes com histórico de versões ·{' '}
          {files.data && list.length ? formatBytes(files.data.totalBytes) : 'vazio'}
        </p>
        <div className="row" style={{ gap: 10 }}>
          {list.some((f) => f.isCurrent) ? (
            <a className="btn" href={`/api/v1/products/${product.id}/files.zip`} download>
              Baixar atuais (.zip)
            </a>
          ) : (
            <button type="button" className="btn" onClick={() => toast('Nada para baixar ainda')}>
              Baixar atuais (.zip)
            </button>
          )}
          <button type="button" className="btn p" onClick={() => setUploading(true)}>
            <Icon name="plus" size={18} />
            Enviar arquivos
          </button>
        </div>
      </div>

      {diffs.map((d) => (
        <div key={`${d.variantId}-${d.productFileId}`} className="alert">
          <Icon name="alert" />
          <div className="col grow" style={{ gap: 6 }}>
            <p>
              <b>
                {versionName(d.productFileId)} mudou o perfil de impressão da variação{' '}
                {d.variantName}.
              </b>
            </p>
            <p className="sm">
              Por unidade: {d.before ? `${formatGrams(d.before.grams)} → ` : ''}
              <b>{formatGrams(d.after.grams)}</b> ·{' '}
              {d.before ? `${formatHm(d.before.printSeconds)} → ` : ''}
              <b>{formatHm(d.after.printSeconds)}</b>
              {d.after.unitCostCentavos !== null
                ? `. Custo unitário ${d.before?.unitCostCentavos != null ? `${formatBRL(d.before.unitCostCentavos)} → ` : ''}${formatBRL(d.after.unitCostCentavos)}.`
                : '.'}
            </p>
            <div className="row wrap" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setDiffs((x) => x.filter((y) => y !== d));
                  toast(`Perfil da ${d.variantName} mantido`);
                }}
              >
                Manter perfil
              </button>
              <button
                type="button"
                className="btn sm p"
                disabled={apply.isPending}
                onClick={() => apply.mutate(d)}
              >
                Atualizar perfil
              </button>
            </div>
          </div>
        </div>
      ))}
      {dups.map((d) => (
        <div key={d.fileId} className="alert warn">
          <Icon name="alert" />
          <div className="col grow" style={{ gap: 6 }}>
            <p>
              <b>{d.name}</b> é idêntico a um arquivo que já existe (v{d.version}, mesmo hash).
              Enviar mesmo assim?
            </p>
            <div className="row wrap" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  void api.delete(`/files/${d.fileId}`, OkResponse).catch(() => undefined);
                  setDups((x) => x.filter((y) => y !== d));
                  toast('Envio cancelado');
                }}
              >
                Cancelar envio
              </button>
              <button type="button" className="btn sm p" onClick={() => keepCopy.mutate(d)}>
                Enviar como cópia
              </button>
            </div>
          </div>
        </div>
      ))}

      <section className="card flat" style={{ overflowX: 'auto' }}>
        <div className="gt" style={{ minWidth: 820 }}>
          <div className="gtr head" style={{ gridTemplateColumns: cols }}>
            <span>Arquivo</span>
            <span>Versão</span>
            <span>Nota da mudança</span>
            <span>Enviado</span>
            <span className="num">Tamanho</span>
            <span />
          </div>
          {files.isPending ? <div className="skel" style={{ height: 120, margin: 16 }} /> : null}
          {PRODUCT_FILE_GROUPS.map((g) => {
            const rows = list.filter((f) => f.group === g.group);
            return (
              <div key={g.group}>
                <div className="gtr gtgroup" style={{ gridTemplateColumns: '1fr' }}>
                  <span style={{ fontWeight: 600 }}>{g.label}</span>
                </div>
                {files.data && rows.length === 0 ? (
                  <div className="gtr" style={{ gridTemplateColumns: '1fr auto' }}>
                    <span className="sm muted">{g.empty}</span>
                    <button type="button" className="btn sm" onClick={() => setUploading(true)}>
                      Enviar
                    </button>
                  </div>
                ) : null}
                {rows.map((f) => (
                  <div key={f.id} className="gtr" style={{ gridTemplateColumns: cols }}>
                    <span className="row" style={{ gap: 10, minWidth: 0 }}>
                      {g.group === 'sliced' ? (
                        f.thumbnailFileId ? (
                          <img
                            className="thumb"
                            src={fileContentUrl(f.thumbnailFileId)}
                            alt=""
                            width={40}
                            height={40}
                          />
                        ) : (
                          <span className="ph" style={{ width: 40, height: 40, flex: '0 0 40px' }}>
                            placa
                          </span>
                        )
                      ) : null}
                      <span style={{ minWidth: 0 }}>
                        <span
                          className="trunc"
                          style={{ display: 'block', fontWeight: f.isCurrent ? 600 : 400 }}
                          title={f.name}
                        >
                          {f.name}
                        </span>
                        {f.usedBy.length ? (
                          <span className="xs muted">
                            perfil de {f.usedBy.map((u) => u.name).join(', ')}
                          </span>
                        ) : f.variant ? (
                          <span className="xs muted">variação {f.variant.name}</span>
                        ) : null}
                      </span>
                    </span>
                    <span>
                      <span className={f.isCurrent ? 'badge solid' : 'badge'}>
                        v{f.version}
                        {f.isCurrent ? ' · atual' : ''}
                      </span>
                    </span>
                    <span className="sm">{f.changeNote ?? '—'}</span>
                    <span className="sm tab">
                      {shortDate(f.createdAt)}
                      {f.uploadedBy ? ` · ${f.uploadedBy.split(' ')[0]}` : ''}
                    </span>
                    <span className="num sm">{formatBytes(f.file.sizeBytes)}</span>
                    <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {!f.isCurrent ? (
                        <button
                          type="button"
                          className="btn sm g"
                          onClick={() => makeCurrent.mutate(f.id)}
                        >
                          Tornar atual
                        </button>
                      ) : null}
                      <a className="btn sm" href={`${f.file.url}?download=1`} download={f.name}>
                        Baixar
                      </a>
                      {isAdmin ? (
                        <button
                          type="button"
                          className="btn icon sm g"
                          aria-label={`Excluir ${f.name} v${f.version}`}
                          title={
                            f.usedBy.length ? 'Usada no perfil de uma variação' : 'Excluir versão'
                          }
                          disabled={f.usedBy.length > 0}
                          onClick={() => remove.mutate(f.id)}
                        >
                          <Icon name="close" size={16} />
                        </button>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>
      {uploading ? (
        <UploadDialog
          product={product}
          existing={list}
          onClose={() => setUploading(false)}
          onSaved={(results) => {
            setUploading(false);
            refresh();
            setDiffs((d) => [...d, ...results.flatMap((r) => r.result?.profileDiffs ?? [])]);
            setDups((d) => [
              ...d,
              ...results.flatMap((r) =>
                r.duplicate && r.pending.stored
                  ? [
                      {
                        fileId: r.pending.stored.id,
                        name: r.pending.file.name,
                        version: r.duplicate.version,
                        group: r.pending.group,
                      },
                    ]
                  : [],
              ),
            ]);
            const saved = results.filter((r) => r.result);
            const failed = results.filter((r) => r.pending.error);
            if (saved.length) {
              toast(
                saved.length === 1
                  ? `${saved[0]!.result!.file.name} v${saved[0]!.result!.file.version} salvo`
                  : `${saved.length} arquivos salvos`,
              );
            }
            if (failed.length)
              toast(`${failed[0]!.pending.file.name}: ${failed[0]!.pending.error}`);
          }}
        />
      ) : null}
    </div>
  );
}
